/**
 * Giftbox Bundle — scoped custom element for templates/product.giftbox_bundle.json.
 * Renders selection panels from server-rendered data and adds the gift box
 * plus the chosen book/candle as nested cart lines via parent_id.
 *
 * Deliberately self-contained: no globals, no MutationObserver, no changes
 * to product-form.js / custom.js / book-info.liquid / candle-info.liquid.
 */
(function () {
  if (customElements.get('giftbox-bundle')) return;

  function formatMoney(cents, format) {
    var value = (cents / 100).toFixed(2);
    var parts = value.split('.');

    function withComma(str) {
      return str.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
    }

    var noDecimals = withComma(parts[0]);
    var withDecimals = noDecimals + '.' + parts[1];

    var result = (format || '${{amount}}')
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDecimals)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withDecimals)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDecimals)
      .replace(/\{\{\s*amount\s*\}\}/g, withDecimals);

    return result;
  }

  class GiftboxBundleElement extends HTMLElement {
    connectedCallback() {
      this.parentVariantId = Number(this.dataset.productVariantId);
      this.basePriceCents = Number(this.dataset.basePrice);
      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      this.cartAddUrl = this.dataset.cartAddUrl;
      this.cartUpdateUrl = this.dataset.cartUpdateUrl;
      this.cartUrl = this.dataset.cartUrl;
      this.currentUrl = this.dataset.currentUrl || window.location.pathname;

      this.bookInputs = Array.prototype.slice.call(this.querySelectorAll('[data-role="book-input"]'));
      this.candleInputs = Array.prototype.slice.call(this.querySelectorAll('[data-role="candle-input"]'));
      this.panels = {
        book: this.querySelector('[data-panel="book"]'),
        candle: this.querySelector('[data-panel="candle"]'),
      };
      this.totalEl = this.querySelector('[data-role="total"]');
      this.guidanceEl = this.querySelector('[data-role="bar-guidance"]');
      this.errorEl = this.querySelector('[data-role="error"]');
      this.submitButton = this.querySelector('[data-role="submit"]');
      this.submitButtonText = this.submitButton.querySelector('[data-role="submit-text"]');
      this.spinner = this.submitButton.querySelector('.loading__spinner');

      this.selected = { book: null, candle: null };

      this.bookInputs.forEach(
        function (input) {
          input.addEventListener('change', this.onSelect.bind(this, 'book', input));
        }.bind(this)
      );
      this.candleInputs.forEach(
        function (input) {
          input.addEventListener('change', this.onSelect.bind(this, 'candle', input));
        }.bind(this)
      );

      this.submitButton.addEventListener('click', this.onSubmit.bind(this));
    }

    onSelect(type, input) {
      this.selected[type] = {
        variantId: Number(input.dataset.variantId),
        priceCents: Number(input.dataset.price),
        title: input.dataset.title,
        image: input.dataset.image,
      };
      this.renderPanel(type);
      this.updateTotal();
      this.updateSubmitState();
      this.clearError();
    }

    renderPanel(type) {
      var data = this.selected[type];
      var panel = this.panels[type];
      if (!panel || !data) return;

      var titleEl = panel.querySelector('[data-role="title"]');
      var imageEl = panel.querySelector('[data-role="image"]');
      var descEl = panel.querySelector('[data-role="description"]');

      titleEl.textContent = data.title || titleEl.textContent;

      if (data.image) {
        imageEl.src = data.image;
        imageEl.alt = data.title || '';
        imageEl.hidden = false;
      } else {
        imageEl.hidden = true;
        imageEl.removeAttribute('src');
      }

      var template = this.querySelector('template[data-description-for="' + data.variantId + '"]');
      var html = template ? template.innerHTML.trim() : '';
      descEl.innerHTML = html || 'אין תיאור זמין עבור פריט זה.';
    }

    updateTotal() {
      var bothSelected = Boolean(this.selected.book && this.selected.candle);

      if (!bothSelected) {
        this.totalEl.hidden = true;
        if (this.guidanceEl) this.guidanceEl.hidden = false;
        return;
      }

      var total = this.basePriceCents + this.selected.book.priceCents + this.selected.candle.priceCents;

      this.totalEl.textContent = 'סה"כ: ' + formatMoney(total, this.moneyFormat);
      this.totalEl.hidden = false;
      if (this.guidanceEl) this.guidanceEl.hidden = true;
    }

    updateSubmitState() {
      var ready = Boolean(this.selected.book && this.selected.candle);
      this.submitButton.toggleAttribute('disabled', !ready);
      this.submitButton.setAttribute('aria-disabled', String(!ready));
    }

    clearError() {
      this.errorEl.hidden = true;
      this.errorEl.textContent = '';
    }

    showError(message) {
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    }

    setLoading(isLoading) {
      if (this.spinner) this.spinner.classList.toggle('hidden', !isLoading);
      var disabled = isLoading || !(this.selected.book && this.selected.candle);
      this.submitButton.toggleAttribute('disabled', disabled);
      this.submitButton.setAttribute('aria-disabled', String(disabled));
    }

    getCartComponent() {
      return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    }

    getSectionDescriptors(cartComponent) {
      if (cartComponent && typeof cartComponent.getSectionsToRender === 'function') {
        return cartComponent.getSectionsToRender();
      }
      return [];
    }

    applySections(sectionDescriptors, sectionsData) {
      if (!sectionsData) return;
      sectionDescriptors.forEach(function (descriptor) {
        var html = sectionsData[descriptor.id];
        if (!html) return;

        var targetEl = descriptor.selector
          ? document.querySelector(descriptor.selector)
          : document.getElementById(descriptor.id);
        if (!targetEl) return;

        var parsedDoc = new DOMParser().parseFromString(html, 'text/html');
        var parsedTarget = descriptor.selector
          ? parsedDoc.querySelector(descriptor.selector)
          : parsedDoc.getElementById(descriptor.id);
        if (parsedTarget) targetEl.innerHTML = parsedTarget.innerHTML;
      });
    }

    refreshCartSections(cartComponent, sectionDescriptors) {
      if (!cartComponent || !sectionDescriptors.length || !this.cartUpdateUrl) {
        return Promise.resolve();
      }

      var payload = {
        updates: {},
        sections: sectionDescriptors.map(function (d) { return d.id; }).join(','),
        sections_url: this.currentUrl,
      };

      return fetch(this.cartUpdateUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      })
        .then(function (response) { return response.json(); })
        .then(
          function (data) {
            this.applySections(sectionDescriptors, data.sections);
          }.bind(this)
        )
        .catch(function (error) {
          console.error('giftbox-bundle: cart refresh failed', error);
        });
    }

    onSubmit(evt) {
      evt.preventDefault();
      if (this.submitButton.hasAttribute('disabled')) return;
      if (!this.selected.book || !this.selected.candle) return;

      this.setLoading(true);
      this.clearError();

      var cartComponent = this.getCartComponent();
      var sectionDescriptors = this.getSectionDescriptors(cartComponent);

      var payload = {
        items: [
          { id: this.parentVariantId, quantity: 1 },
          { id: this.selected.book.variantId, quantity: 1, parent_id: this.parentVariantId },
          { id: this.selected.candle.variantId, quantity: 1, parent_id: this.parentVariantId },
        ],
      };

      if (sectionDescriptors.length) {
        payload.sections = sectionDescriptors.map(function (d) { return d.id; }).join(',');
        payload.sections_url = this.currentUrl;
      }

      fetch(this.cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      })
        .then(function (response) {
          return response.json().then(function (data) {
            return { ok: response.ok, data: data };
          });
        })
        .then(
          function (result) {
            if (!result.ok || result.data.status) {
              this.showError('אחד מהפריטים שנבחרו אזל מהמלאי בינתיים. אנא בחרו אפשרות אחרת ונסו שוב.');
              return this.refreshCartSections(cartComponent, sectionDescriptors);
            }

            if (cartComponent) {
              this.applySections(sectionDescriptors, result.data.sections);
              if (typeof cartComponent.open === 'function') cartComponent.open();
            } else if (this.cartUrl) {
              window.location = this.cartUrl;
            }
          }.bind(this)
        )
        .catch(
          function (error) {
            console.error('giftbox-bundle: add to cart failed', error);
            this.showError('אירעה שגיאה בהוספה לסל. אנא נסו שוב.');
          }.bind(this)
        )
        .finally(
          function () {
            this.setLoading(false);
          }.bind(this)
        );
    }
  }

  customElements.define('giftbox-bundle', GiftboxBundleElement);
})();
